import { useCallback, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import useApiPost from "Pods/Shared/Hooks/useApiPost"
import { getFileMetadata, uploadToDirectUrl } from "Pods/ThreeDStudio/Hooks/directUpload"
import useTranslation from "Pods/ThreeDStudio/Hooks/useTranslation"

import { BACKGROUND_IMAGES_ENDPOINT, BACKGROUND_IMAGES_QUERY_KEY } from "./useBackgroundImages"

function pickImageFields(payload) {
  return {
    id: payload.id,
    blob_id: payload.blob_id,
    background_type: payload.background_type,
    label: payload.label,
    filename: payload.filename,
    background_url: payload.background_url,
    used_in_sessions_count: payload.used_in_sessions_count,
    created_at: payload.created_at,
  }
}

function extractErrorMessage(error, fallbackMessage) {
  const errors = error?.response?.data?.errors
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(". ")
  }
  return fallbackMessage
}

function useUploadBackgroundImage() {
  const t = useTranslation()
  const queryClient = useQueryClient()
  const { postData, error } = useApiPost()
  // useApiPost.isLoading only covers the metadata POST; this flag spans
  // getFileMetadata + POST + uploadToDirectUrl so the upload button stays
  // disabled until the S3 upload also finishes.
  const [isUploading, setIsUploading] = useState(false)

  const uploadBackgroundImage = useCallback((file) => {
    setIsUploading(true)

    const createBackgroundImage = (blob) => new Promise((resolve, reject) => {
      postData({
        path: BACKGROUND_IMAGES_ENDPOINT,
        data: { blob },
        onSuccess: resolve,
        onError: reject,
      })
    })

    getFileMetadata(file)
      .then(blob => createBackgroundImage(blob))
      .then(async (response) => {
        const { data } = response
        if (!data?.direct_upload?.url || !data?.direct_upload?.headers) {
          throw new Error("Invalid direct upload response")
        }
        await uploadToDirectUrl(data.direct_upload.url, data.direct_upload.headers, file)
        return data
      })
      .then((data) => {
        const newImage = pickImageFields(data)
        queryClient.setQueryData(BACKGROUND_IMAGES_QUERY_KEY, (prev) => [
          ...(prev ?? []),
          newImage,
        ])
        window.showNotification?.(t('viewer.environment.added', { label: newImage.label }), {
          type: "success",
          autohide: true,
        })
      })
      .catch((e) => {
        window.showNotification?.(
          extractErrorMessage(e, t('viewer.environment.uploadFailed')),
          { type: "error", autohide: true },
        )
      })
      .finally(() => {
        setIsUploading(false)
      })
  }, [postData, queryClient, t])

  return {
    uploadBackgroundImage,
    isUploading,
    error,
  }
}

export default useUploadBackgroundImage
